import { useState } from 'react';
import { useStore } from 'zustand';

import { KubesoloHostResult, KubesoloTest } from '@api/types.gen';

import { PaginationControls } from '@@/PaginationControls';
import { createPersistedStore } from '@@/datatables/types';

import { HostItem } from './HostItem';

/** One page size for every list of the results, kept across visits. */
const settingsStore = createPersistedStore('kubesolo-connection-test');
/** The smallest page size on offer: above it, the controls stay so the size can be changed back. */
const SMALLEST_PAGE_SIZE = 5;

interface Props {
  label: string;
  hosts: Array<KubesoloHostResult>;
  test: KubesoloTest;
  /** For machines under a cause, which already gives the fix */
  hideFix?: boolean;
}

export function HostList({ label, hosts, test, hideFix }: Props) {
  // per list, like a datatable's page index, while the page size is shared
  const [page, setPage] = useState(1);
  const { pageSize, setPageSize } = useStore(settingsStore);

  if (hosts.length === 0) {
    return <p className="text-muted">No machines.</p>;
  }

  const pageCount = Math.ceil(hosts.length / pageSize);
  // re-tested machines leave the list, which can leave the page empty
  const currentPage = Math.min(page, pageCount);
  const pageHosts = hosts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="mb-3">
      <ul
        aria-label={label}
        className="m-0 list-none divide-y divide-solid divide-gray-3 p-0 th-highcontrast:divide-white th-dark:divide-gray-9"
      >
        {pageHosts.map((host) => (
          <HostItem key={host.id} host={host} test={test} hideFix={hideFix} />
        ))}
      </ul>

      {hosts.length > SMALLEST_PAGE_SIZE && (
        <div className="mt-2 flex justify-end">
          <PaginationControls
            page={currentPage}
            pageCount={pageCount}
            pageLimit={pageSize}
            onPageChange={setPage}
            onPageLimitChange={setPageSize}
            // outside a datatable footer, nothing resets Bootstrap's pagination margin
            className="[&_.form-inline]:items-center [&_.form-inline]:gap-2 [&_.pagination]:!m-0"
          />
        </div>
      )}
    </div>
  );
}
